import { OUTLOOK_COPY, TARGET_OUTLOOK_PATH } from "@/revenue-intelligence/target-outlook/copy";
import { FORECAST_COPY, FORECAST_EXPERIENCE_PATH } from "@/revenue-intelligence/forecast-experience/copy";
import { getPipelineHealthAnalysis, PIPELINE_HEALTH_ANALYSES, PIPELINE_HEALTH_L2_COPY, PIPELINE_HEALTH_L2_PATH, pipelineHealthPath } from "@/revenue-intelligence/pipeline-health-l2/copy";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { getTimePeriodFromDateRange } from "@/lib/app";
import { getRoute, getScopeRoute } from "@/lib/routes";
import { useAppStore } from "@/stores";
import {
  FORECAST_DIAGNOSIS_PATH,
  REVENUE_INTELLIGENCE_AGENTS_PATH,
  getRevenueIntelligencePeriod,
} from "@/revenue-intelligence/lib/revenue-intelligence-routes";
import { DomainType, ScopeType } from "@revelate/types";
import { Link, Route, Switch, useSearch } from "wouter";

interface BreadcrumbFragmentProps {
  label: string;
  href: string;
  withSeparator?: boolean;
}

const BreadcrumbFragment = ({
  label,
  href,
  withSeparator,
}: BreadcrumbFragmentProps) => {
  return (
    <>
      {withSeparator && <BreadcrumbSeparator />}

      <BreadcrumbItem>
        <BreadcrumbLink asChild>
          <Link href={href}>{label}</Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
    </>
  );
};

export const Breadcrumbs = () => {
  const dateRange = useAppStore((state) => state.dateRange);
  const scope = useAppStore((state) => state.scope);
  const search = useSearch();
  const currentParams = new URLSearchParams(search);
  const dealDetailsViewSource = currentParams.get("dealDetailsViewSource");
  const stateOfSalesV2ReturnParams = (() => {
    return new URLSearchParams({
      period: getRevenueIntelligencePeriod(search),
      pipeline: currentParams.get("pipeline") ?? "all",
      team: currentParams.get("team") ?? "all",
      owner: currentParams.get("owner") ?? "all",
      accumulated: currentParams.get("accumulated") ?? "false",
      breakdown: currentParams.get("breakdown") ?? "team",
    }).toString();
  })();
  const healthAnalysis = getPipelineHealthAnalysis(currentParams.get("pipelineHealthView") ?? "");
  const dealDetailsParent = dealDetailsViewSource === "pipeline-health-l2"
    ? { label: healthAnalysis ? PIPELINE_HEALTH_ANALYSES[healthAnalysis].label : PIPELINE_HEALTH_L2_COPY.title, href: pipelineHealthPath(healthAnalysis, stateOfSalesV2ReturnParams) }
    : dealDetailsViewSource === "forecast-v2"
    ? { label: FORECAST_COPY.title, href: `${FORECAST_EXPERIENCE_PATH}?${stateOfSalesV2ReturnParams}` }
    : dealDetailsViewSource === "forecast-diagnosis"
    ? {
      label: "Forecast",
      href: `/revenue-intelligence/state-of-sales-v2/forecast?${stateOfSalesV2ReturnParams}`,
    }
    : dealDetailsViewSource === "state-of-sales-v2-pipeline-risk"
    ? {
      label: "Pipeline Risk",
      href: `/revenue-intelligence/state-of-sales-v2/pipeline-risk?${stateOfSalesV2ReturnParams}`,
    }
    : dealDetailsViewSource === "state-of-sales-v2-pipeline-health"
    ? {
      label: "Pipeline Health",
      href: `/revenue-intelligence/state-of-sales-v2/pipeline-health?${stateOfSalesV2ReturnParams}`,
    }
    : dealDetailsViewSource === "state-of-sales-v2"
    ? {
      label: "Forecast",
      href: `/revenue-intelligence/state-of-sales-v2/forecast?${stateOfSalesV2ReturnParams}`,
    }
    : dealDetailsViewSource === "management-review"
    ? {
      label: "Management Review",
      href: "/revenue-intelligence/management-review",
    }
    : dealDetailsViewSource === "revenue-intelligence-home"
    ? {
      label: "Home",
      href: "/home",
    }
    : {
      label: "Deal Review",
      href: "/revenue-intelligence/deal-review",
    };

  return (
    <Breadcrumb className="flex min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <BreadcrumbList className="flex-nowrap whitespace-nowrap">
        {/* First fragment */}
        <Switch>
          <Route path="/home">
            <BreadcrumbFragment label="Home" href="/home" />
          </Route>

          <Route path={REVENUE_INTELLIGENCE_AGENTS_PATH}>
            <BreadcrumbFragment label="Agents" href={REVENUE_INTELLIGENCE_AGENTS_PATH} />
          </Route>

          <Route path="/state-of-sales">
            <BreadcrumbFragment label="State of Sales" href="/state-of-sales" />
          </Route>

          <Route path={FORECAST_DIAGNOSIS_PATH}>
            <BreadcrumbFragment
              label="Forecast"
              href={`/revenue-intelligence/state-of-sales-v2/forecast?${stateOfSalesV2ReturnParams}`}
            />
          </Route>

          <Route path="/reports/*?">
            <BreadcrumbFragment label="Reports" href="/reports" />
          </Route>

          <Route path="/plans/*?">
            <BreadcrumbFragment label="Compensation Plans" href="/plans" />
          </Route>

          <Route path="/quotas/*?">
            <BreadcrumbFragment label="Quotas" href="/quotas" />
          </Route>

          <Route path="/currencies/*?">
            <BreadcrumbFragment label="Currencies" href="/currencies" />
          </Route>

          <Route path="/users/*?">
            <BreadcrumbFragment label="Users" href="/users" />
          </Route>

          <Route path="/teams/*?">
            <BreadcrumbFragment label="Teams" href="/teams" />
          </Route>

          <Route path="/deals/*?">
            <BreadcrumbFragment label="Deals" href="/deals" />
          </Route>

          <Route path="/integrations/*?">
            <BreadcrumbFragment label="Integrations" href="/integrations" />
          </Route>

          <Route path="/revenue-intelligence/state-of-sales-v2/*?">
            <BreadcrumbFragment
              label="State of Sales"
              href={`/state-of-sales?${stateOfSalesV2ReturnParams}`} />
          </Route>

          <Route path="/revenue-intelligence/*?">
            <BreadcrumbFragment
              label="Revenue Intelligence"
              href="/home" />
          </Route>

          <Route>
            <BreadcrumbFragment label="Dashboard" href="/dashboard" />
          </Route>
        </Switch>

        {/* Deeper fragments */}
        <>
          <Switch>
            <Route path={`${PIPELINE_HEALTH_L2_PATH}/:analysis`}>{params => {
              const analysis = getPipelineHealthAnalysis(params.analysis);
              return <><BreadcrumbFragment withSeparator label={PIPELINE_HEALTH_L2_COPY.title} href={pipelineHealthPath(null, stateOfSalesV2ReturnParams)} />{analysis && <BreadcrumbFragment withSeparator label={PIPELINE_HEALTH_ANALYSES[analysis].label} href={pipelineHealthPath(analysis, stateOfSalesV2ReturnParams)} />}</>;
            }}</Route>
            <Route path={PIPELINE_HEALTH_L2_PATH}>
              <BreadcrumbFragment withSeparator label={PIPELINE_HEALTH_L2_COPY.title} href={`${PIPELINE_HEALTH_L2_PATH}?${stateOfSalesV2ReturnParams}`} />
            </Route>
            <Route path={FORECAST_EXPERIENCE_PATH}>
              <BreadcrumbFragment withSeparator label={FORECAST_COPY.title} href={`${FORECAST_EXPERIENCE_PATH}?${stateOfSalesV2ReturnParams}`} />
            </Route>
            <Route path={TARGET_OUTLOOK_PATH}>
              <BreadcrumbFragment withSeparator label={OUTLOOK_COPY.title} href={TARGET_OUTLOOK_PATH} />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/forecast">
              <BreadcrumbFragment
                withSeparator
                label="Forecast"
                href={`/revenue-intelligence/state-of-sales-v2/forecast?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/pipeline-risk">
              <BreadcrumbFragment
                withSeparator
                label="Pipeline Risk"
                href={`/revenue-intelligence/state-of-sales-v2/pipeline-risk?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/pipeline-health">
              <BreadcrumbFragment
                withSeparator
                label="Pipeline Health"
                href={`/revenue-intelligence/state-of-sales-v2/pipeline-health?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/growth-efficiency">
              <BreadcrumbFragment
                withSeparator
                label="Sales Economics"
                href={`/revenue-intelligence/state-of-sales-v2/sales-economics?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/growth-efficiency-2">
              <BreadcrumbFragment
                withSeparator
                label="Sales Economics"
                href={`/revenue-intelligence/state-of-sales-v2/sales-economics?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
            <Route path="/revenue-intelligence/state-of-sales-v2/sales-economics">
              <BreadcrumbFragment
                withSeparator
                label="Sales Economics"
                href={`/revenue-intelligence/state-of-sales-v2/sales-economics?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/dashboard">
              <BreadcrumbFragment
                withSeparator
                label="State of Sales"
                href={`/state-of-sales?${stateOfSalesV2ReturnParams}`}
              />
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/forecast">
              <>
                <BreadcrumbFragment
                  withSeparator
                  label="State of Sales"
                  href={`/state-of-sales?${stateOfSalesV2ReturnParams}`}
                />
                <BreadcrumbFragment
                  withSeparator
                  label="Forecast"
                  href="/revenue-intelligence/forecast"
                />
              </>
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/pipeline-risk">
              <>
                <BreadcrumbFragment
                  withSeparator
                  label="State of Sales"
                  href={`/state-of-sales?${stateOfSalesV2ReturnParams}`}
                />
                <BreadcrumbFragment
                  withSeparator
                  label="Pipeline Risk"
                  href="/revenue-intelligence/pipeline-risk"
                />
              </>
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/pipeline-coverage">
              <>
                <BreadcrumbFragment
                  withSeparator
                  label="State of Sales"
                  href={`/state-of-sales?${stateOfSalesV2ReturnParams}`}
                />
                <BreadcrumbFragment
                  withSeparator
                  label="Pipeline Coverage"
                  href="/revenue-intelligence/pipeline-coverage"
                />
              </>
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/deal-review">
              <BreadcrumbFragment
                withSeparator
                label="Deal Review"
                href="/revenue-intelligence/deal-review"
              />
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/management-review">
              <BreadcrumbFragment
                withSeparator
                label="Management Review"
                href="/revenue-intelligence/management-review"
              />
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/deal/:id">
              {(params) => (
                <>
                  <BreadcrumbFragment
                    withSeparator
                    label={dealDetailsParent.label}
                    href={dealDetailsParent.href}
                  />
                  <BreadcrumbFragment
                    withSeparator
                    label="Deal"
                    href={`/revenue-intelligence/deal/${params.id}`}
                  />
                </>
              )}
            </Route>
          </Switch>

          <Switch>
            <Route path="/revenue-intelligence/history">
              <BreadcrumbFragment
                withSeparator
                label="History"
                href="/revenue-intelligence/history"
              />
            </Route>
          </Switch>

          <Switch>
            <Route path="/reports/:type?/:id?/*?">
              <BreadcrumbFragment
                withSeparator
                label={getTimePeriodFromDateRange(dateRange)}
                href="/reports"
              />
            </Route>
          </Switch>

          <Route path="/:domain/:type/:id/*?">
            {(params) => params.domain !== "revenue-intelligence" ? (
              <BreadcrumbFragment
                withSeparator
                label={scope?.label || "Add new item"}
                href={
                  scope && params.domain
                    ? getScopeRoute(params.domain as DomainType, scope)
                    : "/"
                }
              />
            ) : <></>}
          </Route>

          <Switch>
            <Route path="/:domain/:type/:id/users">
              {(params) => (
                <BreadcrumbFragment
                  withSeparator
                  label="Users"
                  href={getRoute(
                    params.domain as DomainType,
                    params.type as ScopeType,
                    params.id,
                    "users"
                  )}
                />
              )}
            </Route>
            <Route path="/quotas/quota/:id/goals/*?">
              {(params) => (
                <BreadcrumbFragment
                  withSeparator
                  label="Sales Goals"
                  href={getRoute("quotas", "quota", params.id, "goals")}
                />
              )}
            </Route>
            <Route path="/plans/plan/:id/accelerators/*?">
              {(params) => (
                <BreadcrumbFragment
                  withSeparator
                  label="Accelerators"
                  href={getRoute("plans", "plan", params.id, "accelerators")}
                />
              )}
            </Route>
            <Route path="/:domain/:type/:id">
              {(params) =>
                params.domain !== "dashboard" && params.domain !== "reports" && params.domain !== "revenue-intelligence" ? (
                  <BreadcrumbFragment
                    withSeparator
                    label="General"
                    href={getRoute(
                      params.domain as DomainType,
                      params.type as ScopeType,
                      params.id
                    )}
                  />
                ) : (
                  <></>
                )
              }
            </Route>
          </Switch>

          <Switch>
            <Route path="/plans/plan/:id/accelerators/edit/:acceleratorId">
              {(params) => (
                <BreadcrumbFragment
                  withSeparator
                  label="Edit"
                  href={`/plans/${params.id}/accelerators/edit/${params.acceleratorId}`}
                />
              )}
            </Route>
          </Switch>
        </>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
